import React from "react";
import { IconDropdown } from "../../icon/Dropdown";
import { IconUpload } from "../../icon/Upload";
import "./FormField.css";
import "../../icon/IconSize24px.css";

export type FormFieldType = "input" | "dropdown" | "upload";

interface BaseFormFieldProps {
  type: FormFieldType;
  label?: string;
  data: string;
  placeholder?: string;
}

interface InputFormFieldProps extends BaseFormFieldProps {
  type: "input";
  placeholder: string;
}

interface DropdownFormFieldProps extends BaseFormFieldProps {
  type: "dropdown";
  label: string;
  options: { value: string; label: string }[];
}

interface UploadFormFieldProps extends BaseFormFieldProps {
  type: "upload";
  label: string;
}

export type FormFieldProps = InputFormFieldProps | DropdownFormFieldProps | UploadFormFieldProps;

/**
 * A unified FormField component that supports multiple input types:
 * - input: Standard text input field
 * - dropdown: Select dropdown with options
 * - upload: File upload field for images
 */
export const FormField: React.FC<FormFieldProps> = (props) => {
  const { type, label, data } = props;
  const className = `form-field form-field-${type}`;

  // Input type
  if (type === "input") {
    const { placeholder } = props as InputFormFieldProps;
    return (
      <div className={className}>
        {label && (
          <label className="navigated-text-normal" htmlFor={data}>
            {label}
          </label>
        )}
        <input
          type="text"
          className="small-text-normal"
          placeholder={placeholder}
          id={data}
          name={data}
        />
      </div>
    );
  }

  // Dropdown type
  if (type === "dropdown") {
    const { options } = props as DropdownFormFieldProps;
    return (
      <div className={className}>
        <label className="navigated-text-normal" htmlFor={data}>
          {label}
        </label>
        <select className="small-text-normal" id={data} name={data}>
          {options.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <IconDropdown className="icon-size-24px" />
      </div>
    );
  }

  // Upload type
  if (type === "upload") {
    return (
      <div className={className}>
        <label className="navigated-text-normal" htmlFor={data}>
          {label}
        </label>
        <div>
          <input
            type="file"
            accept="image/png, image/jpeg"
            multiple
            id={data}
            name={data}
            className="small-text-normal"
          />
          <IconUpload className="icon-size-24px" />
        </div>
      </div>
    );
  }

  return null;
};
